import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, map, catchError, tap, take, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { ContactsService } from 'src/app/core/lists/_services';
import {
  loadContacts,
  loadContactsFailure,
  loadContactsSuccess,
  toggleContactsListPageLoading,
  updateContact,
  updateContactFailure,
  updateContactSuccess,
} from '../actions/contact-duplicate-cell-phone.actions';
import { select, Store } from '@ngrx/store';
import { ContactModel } from 'src/app/core/lists';
import {
  selectContacts,
  selectSelectedCellPhone,
} from '../selectors/contact-duplicate-cell-phone.selectors';
import { LayoutUtility } from 'src/app/shared/utils';

@Injectable()
export class ContactListEffects {
  submissionErrorObj: { error: any }[];
  constructor(
    private actions$: Actions,
    private contactsService: ContactsService,
    private store: Store,
    private layoutUtilsService: LayoutUtility,
  ) {}

  loadContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadContacts),
      tap(() => {
        this.store.dispatch(toggleContactsListPageLoading({ pageLoading: true }));
      }),
      withLatestFrom(this.store.pipe(select(selectSelectedCellPhone))),

      mergeMap(([, cellPhone]) => {
        return this.contactsService.findContactsWithDublicateCellPhone(cellPhone).pipe(
          map((res: any) => {
            return loadContactsSuccess({ contacts: res.data, totalCount: res.count });
          }),
          catchError(() => {
            return of(loadContactsFailure());
          }),
        );
      }),
      tap(() => {
        this.store.dispatch(toggleContactsListPageLoading({ pageLoading: false }));
      }),
    ),
  );

  updateContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateContact),
      mergeMap(({ id, changes }) => {
        return this.store.pipe(
          select(selectContacts),
          take(1),
          map(({ items }) => items.find((c) => c.id === id)),
          mergeMap((existingContact) => {
            if (!existingContact) {
              return of(updateContactFailure({ error: 'Contact not found!' }));
            }

            const updatedContact: ContactModel = {
              ...existingContact,
              ...changes,
              first_name: changes.first_name?.trim() || existingContact.first_name || 'Unknown',
              last_name: changes.last_name?.trim() || existingContact.last_name || 'Unknown',
              clear: existingContact.clear ?? null,
              changeActiveStatus: existingContact.changeActiveStatus ?? null,
              _createdDate: existingContact._createdDate || new Date().toISOString(),
              _updatedDate: new Date().toISOString(),
            };

            return this.contactsService.updateContact(updatedContact).pipe(
              map((contact: any) => {
                this.layoutUtilsService.showActionNotification(
                  'INFO',
                  'Cell phone updated successfully!',
                );
                const updatedContact = contact.data;
                return updateContactSuccess({ contact: updatedContact });
              }),
              catchError((error) => {
                let errorMessage = 'Failed to update contact';

                if (error.error?.errors?.[0]?.details) {
                  errorMessage = error.error.errors[0].details;
                }
                this.layoutUtilsService.showActionNotification('ERROR', `Error: ${errorMessage}`);

                return of(updateContactFailure({ error }));
              }),
            );
          }),
        );
      }),
    ),
  );
}
